import PaginatedTable from '../../components/ui/PaginatedTable';
import { useState, useMemo, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { DrillDownModal } from "./DrillDownModal";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

export default function SalesReports() {
  const [from, setFrom] = useState("2026-01-01");
  const [to, setTo] = useState("2026-01-31");
  const [drillData, setDrillData] = useState(null);
  const [drillTitle, setDrillTitle] = useState("");
  const [salesData, setSalesData] = useState([]);

  useEffect(() => {
    loadReport();
  }, [from, to]);

  const loadReport = async () => {
    const res = await fetch(
      `/api/reports/sales?from_date=${from}&to_date=${to}`,
      {
        credentials: "include",
      },
    );

    const data = await res.json();

    if (data.status === "success") {
      setSalesData(data.data);
    }
  };

  const filtered = useMemo(() => {
    return salesData.filter((d) => d.date >= from && d.date <= to);
  }, [from, to]);

  const summary = useMemo(() => {
    let revenue = 0,
      cost = 0,
      qty = 0;

    filtered.forEach((d) => {
      revenue += d.revenue;
      cost += d.cost;
      qty += d.quantity;
    });

    return {
      revenue,
      cost,
      profit: revenue - cost,
      qty,
    };
  }, [filtered]);

  const monthlyPL = useMemo(() => {
    const map = {};

    salesData.forEach((d) => {
      const month = d.date.slice(0, 7);
      if (!map[month]) {
        map[month] = { revenue: 0, cost: 0, items: [] };
      }
      map[month].revenue += d.revenue;
      map[month].cost += d.cost;
      map[month].items.push(d);
    });

    return Object.entries(map).map(([month, v]) => ({
      month,
      revenue: v.revenue,
      cost: v.cost,
      profit: v.revenue - v.cost,
      items: v.items,
    }));
  }, []);

  const categoryTotals = useMemo(() => {
    const map = {};
    filtered.forEach((d) => {
      if (!map[d.category]) {
        map[d.category] = { revenue: 0, cost: 0 };
      }
      map[d.category].revenue += d.revenue;
      map[d.category].cost += d.cost;
    });
    return map;
  }, [filtered]);

  const branchTotals = useMemo(() => {
    const map = {};
    filtered.forEach((d) => {
      if (!map[d.branch]) {
        map[d.branch] = { revenue: 0 };
      }
      map[d.branch].revenue += d.revenue;
    });
    return map;
  }, [filtered]);

  const monthlyChartData = useMemo(() => {
    const map = {};

    filtered.forEach((d) => {
      const month = d.date.slice(0, 7);
      if (!map[month]) {
        map[month] = { month, revenue: 0, cost: 0 };
      }
      map[month].revenue += d.revenue;
      map[month].cost += d.cost;
    });

    return Object.values(map).map((m) => ({
      ...m,
      profit: m.revenue - m.cost,
    }));
  }, [filtered]);

  const exportCSV = () => {
    const rows = [
      [
        "Date",
        "Branch",
        "Category",
        "Item",
        "Qty",
        "Revenue",
        "Cost",
        "Profit",
      ],
      ...filtered.map((d) => [
        d.date,
        d.branch,
        d.category,
        d.item,
        d.quantity,
        d.revenue,
        d.cost,
        d.revenue - d.cost,
      ]),
    ];

    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "sales-report.csv";
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-serif tracking-tight">Sales Report</h1>
            <p className="text-sm text-gray-400 mt-1">
              ERP Overview · Revenue · Profit · Performance
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm"
            />
            <button
              onClick={exportCSV}
              className="px-4 py-2 rounded-lg bg-amber-400 text-black text-sm font-medium"
            >
              Export
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <KpiCard
            label="Revenue"
            value={`₹${summary.revenue.toLocaleString()}`}
            color="text-indigo-400"
          />
          <KpiCard
            label="Cost"
            value={`₹${summary.cost.toLocaleString()}`}
            color="text-rose-400"
          />
          <KpiCard
            label="Profit"
            value={`₹${summary.profit.toLocaleString()}`}
            color="text-emerald-400"
            highlight
          />
          <KpiCard
            label="Units Sold"
            value={summary.qty}
            color="text-amber-400"
          />
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <h2 className="font-serif text-lg mb-4">Monthly Profit & Loss</h2>

          <PaginatedTable className="w-full text-sm">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-2">Month</th>
                <th className="text-right py-2">Revenue</th>
                <th className="text-right py-2">Cost</th>
                <th className="text-right py-2">Profit</th>
              </tr>
            </thead>
            <tbody>
              {monthlyPL.map((m) => (
                <tr
                  key={m.month}
                  className="border-t border-white/10 hover:bg-white/5 cursor-pointer"
                  onClick={() => {
                    setDrillData(m.items);
                    setDrillTitle(`P&L Details — ${m.month}`);
                  }}
                >
                  <td className="py-2">{m.month}</td>
                  <td className="py-2 text-right">₹{m.revenue}</td>
                  <td className="py-2 text-right text-rose-400">₹{m.cost}</td>
                  <td className="py-2 text-right text-emerald-400">
                    ₹{m.profit}
                  </td>
                </tr>
              ))}
            </tbody>
          </PaginatedTable>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">Branch Performance</h2>
            <div className="space-y-3">
              {Object.entries(branchTotals).map(([b, v]) => (
                <div key={b} className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">{b}</span>
                  <span className="text-sm font-medium">
                    ₹{v.revenue.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">Category Profit</h2>
            <div className="space-y-3">
              {Object.entries(categoryTotals).map(([c, v]) => (
                <div key={c} className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">{c}</span>
                  <span className="text-sm text-emerald-400">
                    ₹{(v.revenue - v.cost).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DrillDownModal
          title={drillTitle}
          data={drillData}
          onClose={() => setDrillData(null)}
        />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">Revenue · Cost · Profit</h2>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#6366f1" radius={[6, 6, 0, 0]} />
                <Bar dataKey="cost" fill="#f43f5e" radius={[6, 6, 0, 0]} />
                <Bar dataKey="profit" fill="#22c55e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h2 className="font-serif text-lg mb-4">Profit Trend</h2>

            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#22c55e"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <PaginatedTable className="w-full text-sm">
            <thead className="bg-black/50 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Date</th>
                <th className="px-6 py-4 text-left">Branch</th>
                <th className="px-6 py-4 text-left">Item</th>
                <th className="px-6 py-4 text-right">Revenue</th>
                <th className="px-6 py-4 text-right">Cost</th>
                <th className="px-6 py-4 text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => {
                const profit = d.revenue - d.cost;
                return (
                  <tr
                    key={i}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="px-6 py-3">{d.date}</td>
                    <td className="px-6 py-3">{d.branch}</td>
                    <td className="px-6 py-3 font-medium">{d.item}</td>
                    <td className="px-6 py-3 text-right">
                      ₹{d.revenue.toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-right text-rose-400">
                      ₹{d.cost.toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-right text-emerald-400">
                      ₹{profit.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </PaginatedTable>
        </div>
      </div>
    </DashboardLayout>
  );
}

function KpiCard({ label, value, color, highlight }) {
  return (
    <div
      className={`rounded-2xl bg-white/5 border border-white/10 p-6 ${
        highlight ? "ring-1 ring-emerald-400/30" : ""
      }`}
    >
      <p className="text-xs uppercase tracking-widest text-gray-400">{label}</p>
      <p className={`mt-3 text-2xl font-serif ${color}`}>{value}</p>
    </div>
  );
}
